import classnames from 'classnames'
import { useTranslation } from 'next-i18next'

import { FormattedPrice } from '@src/blocks/currency/currency.component'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper, {
  getUnitForPerServing,
  shouldShowPricePerUnit,
} from '@src/quince-site-common/src/helpers/productHelper'

import styles from './ProductInfoPrice.module.scss'

export interface ProductInfoPriceProps {
  product?: Product
  formattedSalePrice: string
  formattedTraditionalPrice?: string
  savingPercentage?: number
  formattedFestivalOfferPrice?: string
  showDiscount?: boolean
  pricePerServing?: number
  traditionalPricePerServing?: number
  festivalPricePerUnit?: number
  hideSavings?: boolean
  showNewPdpDesignV2?: boolean
  isFinalSaleProduct?: boolean
}

const DIFFERENCE_TO_IGNORE = 0.1

function ProductInfoPrice({
  product,
  formattedSalePrice,
  formattedTraditionalPrice,
  savingPercentage,
  formattedFestivalOfferPrice,
  showDiscount = true,
  pricePerServing,
  traditionalPricePerServing,
  festivalPricePerUnit,
  hideSavings,
  showNewPdpDesignV2,
  isFinalSaleProduct,
}: ProductInfoPriceProps) {
  const { t } = useTranslation('common')
  const isDiscountLabelPresent =
    showDiscount && formattedTraditionalPrice
      ? Number(formattedTraditionalPrice) - Number(formattedSalePrice) > DIFFERENCE_TO_IGNORE
      : false
  const showPricePerUnit = shouldShowPricePerUnit(product)
  const hasPerUnitPricing = showPricePerUnit && !!pricePerServing && !!traditionalPricePerServing
  const perUnitSavingsPercentage = hasPerUnitPricing
    ? productHelper.getSavingPercentage(traditionalPricePerServing, pricePerServing)
    : ''

  const showSavingPercentage =
    !formattedFestivalOfferPrice &&
    formattedTraditionalPrice &&
    savingPercentage &&
    isDiscountLabelPresent &&
    !hasPerUnitPricing &&
    !hideSavings

  const showPerUnitSavingsPercentage = perUnitSavingsPercentage && !formattedFestivalOfferPrice && !hideSavings
  const perUnitLabel = getUnitForPerServing(product)
  const unitForPerServing = perUnitLabel ? t(perUnitLabel) : ''
  return (
    <div>
      <div
        className={classnames(styles.priceWrapper, showNewPdpDesignV2 && styles.priceWrapper_newDesignV2, {
          [`${styles.priceWrapper__festivalOffer}`]: formattedFestivalOfferPrice,
          [`${styles.priceWrapper__festivalOffer_newDesignV2}`]: formattedFestivalOfferPrice && showNewPdpDesignV2,
        })}
      >
        <div className={classnames(styles.priceWrapper__main, styles.priceWrapper__main__space)}>
          <div
            className={classnames(
              styles.priceWrapper__main__value,
              styles.priceWrapper__main__value__qp,
              showNewPdpDesignV2 && styles.priceWrapper__main__value__qp_newDesignV2,
              formattedFestivalOfferPrice &&
                showNewPdpDesignV2 &&
                styles.priceWrapper__main__value__qp_newDesignV2_festivalPrice,
            )}
          >
            <span className={classnames('sales_price_selector_for_force_update')}>
              <FormattedPrice amount={formattedSalePrice} />
            </span>
            {hasPerUnitPricing && !formattedFestivalOfferPrice && unitForPerServing && (
              <span
                className={classnames(
                  styles.priceWrapper__main__value__perserving,
                  showNewPdpDesignV2 && styles.priceWrapper__main__value__perserving_newDesignV2,
                )}
              >
                {'('}
                <span
                  className={classnames(styles.priceWrapper__main__value__perserving__traditional, {
                    [styles.priceWrapper__main__value__perserving__traditional_newPdpDesignV2 ?? '']:
                      showNewPdpDesignV2,
                  })}
                >
                  <FormattedPrice amount={traditionalPricePerServing} />{' '}
                </span>
                <FormattedPrice amount={pricePerServing} /> /{unitForPerServing}
                {')'}
              </span>
            )}
          </div>
          {formattedFestivalOfferPrice && (
            <div
              className={classnames(
                styles.priceWrapper__main__value,
                styles.priceWrapper__main__value__qp,
                styles.festival_offer,
                showNewPdpDesignV2 && styles.festival_offer_newDesignV2,
              )}
            >
              <span className="festival_sale_price_selector_for_force_update">
                <FormattedPrice amount={formattedFestivalOfferPrice} />
              </span>
            </div>
          )}
          {isFinalSaleProduct && (
            <div
              className={classnames(
                styles.priceWrapper__main__finalSale,
                showNewPdpDesignV2 && styles.priceWrapper__main__finalSale_newDesignV2,
              )}
            >
              <span>{t('final_sale')} </span>
            </div>
          )}
          {showSavingPercentage && !showNewPdpDesignV2 && (
            <div className={classnames(styles.priceWrapper__main__text, styles.priceWrapper__main__margin)}>{t('you_save', { percentage: savingPercentage })}</div>
          )}

          {showPerUnitSavingsPercentage && !showNewPdpDesignV2 && (
            <div className={styles.priceWrapper__main__text}>
              {t('save')} <span>{perUnitSavingsPercentage}</span>
              {t('compared_to_traditional_retail')}
            </div>
          )}
        </div>

        {formattedTraditionalPrice && isDiscountLabelPresent && !hasPerUnitPricing && (
          <div className={classnames(styles.priceWrapper__main)}>
            <div
              className={classnames(
                styles.priceWrapper__main__value,
                styles.priceWrapper__main__value__rp,
                formattedFestivalOfferPrice && styles.priceWrapper__main__margin,
                showNewPdpDesignV2 && styles.priceWrapper__main__value__rp_newDesignV2,
                formattedFestivalOfferPrice &&
                  showNewPdpDesignV2 &&
                  styles.priceWrapper__main__value__rp_newDesignV2_festivalPrice,
              )}
            >
              <span className="traditionalPrice_selector_for_force_update">
                {formattedFestivalOfferPrice && showNewPdpDesignV2 ? (
                  t('vs_traditional_retail', { value: formattedTraditionalPrice })
                ) : (
                  <FormattedPrice amount={formattedTraditionalPrice} />
                )}
              </span>
            </div>
            {!showNewPdpDesignV2 && (
              <div className={classnames(styles.priceWrapper__main__text, styles.priceWrapper__main__text__rp, styles.priceWrapper__main__margin)}>
                {t('traditional_retail')}
              </div>
            )}
          </div>
        )}
      </div>
      {showNewPdpDesignV2 && (showSavingPercentage || showPerUnitSavingsPercentage) && (
        <div className={classnames(styles.priceWrapper__main__text, styles.priceWrapper__main__text_newDesignV2, styles.priceWrapper__main__margin)}>
          {t('save')} <span>{showSavingPercentage ? savingPercentage : perUnitSavingsPercentage}</span>
          {t('compared_to_traditional_retail')}
        </div>
      )}

      {festivalPricePerUnit && traditionalPricePerServing && unitForPerServing && (
        <div className={classnames(styles.priceWrapper__main__text, styles.priceWrapper__main__text_newDesignV2)}>
          <span className={styles.priceWrapper__main__value__perserving__traditional}>
            <FormattedPrice amount={traditionalPricePerServing} />
          </span>{' '}
          <FormattedPrice amount={festivalPricePerUnit} /> /{' '}
          {t('per_unit_vs_traditional_retail', { value: unitForPerServing })}
        </div>
      )}
    </div>
  )
}

export default ProductInfoPrice
